<template>
    <div>
		<event-product v-for="(product, productIndex) in products" :key="'product-' + product.id" :product="product" :selected="!!(data && data[product.id])" bordered
					   :disabled="!isSelectable(product)" :class="{'mb-16': productIndex !== products.length - 1}">
			<template #actions>
				<div>
					<lazy-alert v-if="getExistingQuantity(product)" type="success" text small>
						{{ $t('param_text.already_count_purchases', {count: getExistingQuantity(product)}) }}
					</lazy-alert>
					<lazy-alert v-if="product.stock !== null && product.stock <= 0" small class="mt-16">{{ $t('text.product.out_of_stock') }}</lazy-alert>
					<lazy-alert v-else-if="isOutOfMaxQuantity(product)" small class="mt-16">{{ $t('text.product.out_of_max_quantity') }}</lazy-alert>
					<div v-else-if="data && data[product.id]" class="mt-16">
						<div class="max-w-500">
							<form-select v-if="product.multi_quantity"
										 :label="$t('label.quantity')"
										 :placeholder="$t('placeholder.select')"
										 :color="event.color"
										 :info="data[product.id].quantity > 1 && product.action === productActions.GenerateEventCoupon ? $t('help.product.purchase.coupon_multi_quantity', {quantity: data[product.id].quantity}) : null"
										 :disabled="disabled"
										 :rules="{required: true}"
										 v-model="data[product.id].quantity"
										 @update:modelValue="value => handleUpdatedProductQuantity(product, value)"
										 :options="getQuantityOptions(product)"/>

							<template v-if="data[product.id].quantity && product.inputs.length">
								<div v-for="quantityIndex in data[product.id].quantity" :key="`inputs-${product.id}-${quantityIndex}`">
									<p v-if="data[product.id].quantity > 1" class="subheading mb-16">{{ product.name }} {{ quantityIndex }}</p>
									<lazy-custom-form-inputs :inputs="product.inputs" :data="data[product.id].inputs[quantityIndex - 1]" :color="event.color" :disabled="disabled"/>
								</div>
							</template>
						</div>
					</div>

					<lazy-alert v-if="data && data[product.id] && withRequiredProducts && product.is_required" type="info" small>
						{{ $t('text.product.is_required') }}
					</lazy-alert>
					<wrapper v-else grow>
						<btn v-if="!isSelectable(product)" disabled color="success" small>
							<span>{{ getNotSelectableFailure(product) }}</span>
						</btn>
						<btn v-else-if="data && data[product.id]" @click="removeProduct(product)" color="error" outlined
							 small>
							<icon :icon="icons.remove"/>
							<span>{{ $t('action.remove') }}</span>
						</btn>
						<btn v-else @click="addProduct(product)" color="success" small>
							<icon :icon="icons.check"/>
							<span>{{ $t('action.select') }}</span>
						</btn>
					</wrapper>
				</div>
			</template>
		</event-product>
    </div>
</template>

<script>
import {FormInputsMixin} from "@spoferan/nuxt-spoferan/mixins";
import {EventProductAction} from "@spoferan/spoferan-ts-core";
import {getAnalyticsDataForEventProduct,} from "@spoferan/spoferan-ts-core";
import {checkIcon} from "@spoferan/nuxt-spoferan/icons";
import {removeIcon} from "@spoferan/nuxt-spoferan/icons";

export default {
    name: 'EventProductPurchaseFormInputs',

    mixins: [FormInputsMixin],

    props: {

        // The products to display for selection
        products: {
            type: Array,
            required: true
        },

        // The existing purchases as required for allowed quantity validation
        existing: {
            type: Array,
            default: () => []
        },

        // The event to create the purchase for
        event: {
            type: Object,
            required: true
        },

        // The data of the participation the purchases are for
        participation: {
            type: Object,
            default: null
        },

        // Whether to automatically select and force required products
        withRequiredProducts: {
            type: Boolean,
            default: false
        },

        // Analytics data
        itemListId: {
            type: String,
            default: null,
        },

        itemListName: {
            type: String,
            default: null,
        }
    },

    data() {
        return {
            productActions: EventProductAction,

			icons: {
				check: checkIcon,
				remove: removeIcon

			}
        }
    },

    watch: {
        products() {
            this.selectRequiredProducts();
        }
    },

    mounted() {
        this.$nextTick(function () {
            if (this.itemListId) {
                this.$analytics.trackEvent('view_item_list', {
                    item_list_id: this.itemListId,
                    item_list_name: this.itemListName,
                    items: this.products.map((product, productIndex) => getAnalyticsDataForEventProduct(this.event, product, 1, this.getActivePriceForProduct(product)?.cents ?? null, productIndex))
                })
            }

            this.selectRequiredProducts();
        });
    },

    methods: {

        selectRequiredProducts() {
            // Auto-select required products
            if (this.withRequiredProducts) {
                const requiredProducts = this.products.filter(product => !!product.is_required);
                if (requiredProducts && requiredProducts.length) {
                    requiredProducts.forEach(product => {
                        if (!this.data[product.id] && this.isSelectable(product)) {
                            this.addProduct(product);
                        }
                    })
                }
            }
        },

        /**
         * Adds the specified product to the selection
         *
         * @param product
         */
        addProduct(product) {
            const options = this.getQuantityOptions(product);
            const quantity = options[0]?.value ?? 1;
            const activePrice = this.getActivePriceForProduct(product);
            const purchaseData = {
                product: product,
                product_id: product.id,
                activePrice: activePrice,
                quantity: quantity,
                inputs: []
            };

            this.data[product.id] = purchaseData
            this.handleUpdatedProductQuantity(product, quantity)
            this.triggerAddToCartAnalyticsEvent(purchaseData);
        },

        /**
         * Removes the specified product from the selection
         *
         * @param product
         */
        removeProduct(product) {
            this.triggerRemoveFromCartAnalyticsEvent(this.data[product.id]);
            delete this.data[product.id]
        },

        /**
         * Gets the active price of the specified product.
         */
        getActivePriceForProduct(product) {
            const prices = product.prices
            if (prices && prices.length) {
                const activePrices = prices.filter(price => {
                    return price.is_active && !price.for_additional
                })

                if (activePrices && activePrices.length) {

                    // The lowest active price will be taken by default
                    return activePrices.sort((a, b) => a.cents < b.cents ? -1 : 1)[0]
                }
            }

            return null;
        },

        getNotSelectableFailure(product) {
            if (product.status && product.status.value !== 'published') {
                return product.status.title;
            }
            if (product.stock === 0) {
                return this.$t('label.not_in_stock')
            }
            if (this.isOutOfMaxQuantity(product)) {
                return this.$t('label.max_purchases_reached')
            }
            if (!this.getActivePriceForProduct(product)) {
                return this.$t('label.no_active_price')
            }
            return null;
        },

        isSelectable(product) {
            return !this.getNotSelectableFailure(product)
        },

        getExistingQuantity(product) {
            if (!this.existing || !this.existing.length) {
                return 0;
            }

            const existingProductPurchases = this.existing.filter(existing => existing.product_id === product.id);
            return existingProductPurchases && existingProductPurchases.length ? existingProductPurchases.reduce((prev, cur) => prev + cur.quantity, 0) : 0;
        },

        isOutOfMaxQuantity(product) {
            const existingQuantity = this.getExistingQuantity(product);

            return (product.max_quantity !== null && existingQuantity >= product.max_quantity) || (existingQuantity >= 1 && !product.multi_quantity)
        },

        getQuantityOptions(product) {
            const minQuantity = product.min_quantity ?? 1;
            const existingQuantity = this.getExistingQuantity(product);

            let maxQuantity = product.max_quantity ? (product.max_quantity - existingQuantity) : 1000;

            // The user can only select the max available amount of the current active price
            const activePrice = this.getActivePriceForProduct(product);
            if (activePrice && activePrice.available_purchases_count) {
                maxQuantity = Math.min(maxQuantity, activePrice.available_purchases_count)
            }

            maxQuantity = Math.min(maxQuantity, product.stock ?? 1000);

            const options = [];
            for (let i = minQuantity; i <= maxQuantity; i++) {
                options.push({
                    value: i,
                    text: i
                });
            }
            return options;
        },

        handleUpdatedProductQuantity(product, quantity) {
            if (!this.data[product.id].inputs) {
                this.data[product.id]['inputs'] = [];
            }
            const currentQuantity = this.data[product.id].inputs.length;
            if (currentQuantity > quantity) {
                const removedQuantity = currentQuantity - quantity;
                this.data[product.id].inputs.splice(-removedQuantity)
            } else if (currentQuantity < quantity) {
                const addedQuantity = quantity - currentQuantity;
                for (let i = 0; i < addedQuantity; i++) {
                    const addedQuantityInputs = {};
                    product.inputs.forEach(input => {
                        addedQuantityInputs[input.id] = {
							id: input.id,
							value: input.default_value,
							label: input.label,
							type: {...input.type} // Required for the value display preview, especially for booleans
						};
                    });
                    this.data[product.id].inputs.push(addedQuantityInputs);
                }
            }
        },

        triggerAddToCartAnalyticsEvent(purchaseData) {
            if (!this.itemListId) {
                return;
            }

            const centsValue = purchaseData.activePrice ? purchaseData.activePrice.cents : null;
            this.$analytics.trackEvent('add_to_cart', {
                item_list_id: this.itemListId,
                item_list_name: this.itemListName,
                currency: 'EUR',
                value: centsValue ? (Math.round(((centsValue / purchaseData.quantity) / 100) * 100) / 100) : null,
                items: [getAnalyticsDataForEventProduct(this.event, purchaseData.product, purchaseData.quantity, centsValue)]
            });
        },

        triggerRemoveFromCartAnalyticsEvent(purchaseData) {
            if (!this.itemListId) {
                return;
            }

            const centsValue = purchaseData.activePrice ? purchaseData.activePrice.cents : null;
            this.$analytics.trackEvent('remove_from_cart', {
                item_list_id: this.itemListId,
                item_list_name: this.itemListName,
                currency: 'EUR',
                value: centsValue ? (Math.round(((centsValue / purchaseData.quantity) / 100) * 100) / 100) : null,
                items: [getAnalyticsDataForEventProduct(this.event, purchaseData.product, purchaseData.quantity, centsValue)]
            });
        }

    }

}
</script>